import React, {
  FC,
  KeyboardEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import usePlacesAutocomplete, { Suggestion } from 'use-places-autocomplete'
import Spinner from 'react-bootstrap/Spinner'
import _isEmpty from 'lodash/isEmpty'
import GoogleMapsContext from 'context/common/GoogleMapsContext'
import styles from './index.module.scss'
import AutoCompleteSuggestions from './AutoCompleteSuggestions'
import classNames from 'classnames'
import Button from 'react-bootstrap/Button'
import Icon from 'common/Icon'
import { NAVY, SLATE_GRAY } from 'constants/colors'
import { getFirstLocationResultData } from 'utils/common'
import { usedForFeature } from 'constants/logEvents'
import { cn } from '@/lib/utils'
import { stateAbbreviations } from 'app/SeFriendly/validateUsaState'
import { getState } from 'common/Footer/helpers'

type LocationAutoCompleteProps = {
  showClearOption?: boolean
  showSearchIcon?: boolean
  onSelect: (result: GoogleLocationResult) => void
  prefillInputValue?: string
  // For customizations
  containerClassName?: string
  inputClassName?: string
  suggestionContainerClassName?: string
  suggestionItemClassName?: string
  disableAutoFocus?: boolean
  showStateFullname?: boolean
}

const LocationAutoComplete: FC<LocationAutoCompleteProps> = ({
  onSelect,
  showClearOption = false,
  showSearchIcon = false,
  prefillInputValue = '',
  containerClassName = '',
  inputClassName = '',
  suggestionContainerClassName = '',
  suggestionItemClassName = '',
  disableAutoFocus = false,
  showStateFullname = false,
}) => {
  // https://developers.google.com/maps/documentation/javascript/supported_types
  // ^ For more details on what do we want to show in the google autocomplete results
  const searchOptions = {
    types: [
      'locality',
      'sublocality',
      'postal_code',
      'administrative_area_level_1',
      'administrative_area_level_2',
    ],
  }
  const locationInputRef = useRef(null)
  const [inputValue, setInputValue] = useState(prefillInputValue)
  const { googleMapsScriptLoading } = useContext(GoogleMapsContext)
  const {
    init,
    suggestions,
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'us' },
      ...searchOptions,
    },
    debounce: 300,
    initOnMount: false,
    callbackName: 'initMap',
  })

  useEffect(() => {
    if (!disableAutoFocus && locationInputRef.current) {
      locationInputRef.current.focus()
    }
  }, [disableAutoFocus])

  useEffect(() => {
    if (prefillInputValue) {
      setInputValue(prefillInputValue)
    }
  }, [prefillInputValue])

  useMemo(() => {
    // scritly checking for only boolean false value
    if (googleMapsScriptLoading === false) {
      init()
    }
  }, [googleMapsScriptLoading])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setValue(value)
    setInputValue(value)
  }

  const handleSelect = async ({
    place_id,
    structured_formatting,
  }: Suggestion) => {
    try {
      const { firstResult, latLong } = await getFirstLocationResultData(
        place_id,
        usedForFeature.HeaderFilterLocationDropdown
      )

      if (showStateFullname) {
        const state = getState(firstResult)
        const main_text = structured_formatting.main_text

        const stateFullName = stateAbbreviations[state] || state

        let formattedLocation = ''

        if (
          main_text &&
          stateFullName &&
          stateFullName !== structured_formatting.main_text
        ) {
          formattedLocation = `${structured_formatting.main_text}, ${stateFullName}`
        } else if (
          main_text &&
          stateFullName &&
          stateFullName === structured_formatting.main_text
        ) {
          formattedLocation = structured_formatting.main_text
        } else {
          formattedLocation = stateFullName
        }

        structured_formatting = {
          ...structured_formatting,
          main_text: formattedLocation,
          secondary_text: '',
        }
      }

      onSelect({
        ...firstResult,
        ...latLong,
        ...structured_formatting,
      })
      clearSuggestions()
    } catch (error) {
      //TODO: error handling not required
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !_isEmpty(suggestions.data)) {
      handleSelect(suggestions.data[0])
    }
  }

  return (
    <div className={classNames('position-relative d-flex', containerClassName)}>
      <input
        ref={locationInputRef}
        autoComplete="off"
        disabled={googleMapsScriptLoading}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        value={inputValue}
        className={cn(
          '~outline-none ~border-none ~text-base ~w-full',
          inputClassName
        )}
      />

      {suggestions.loading && (
        <div className="align-self-center ml-auto mr-1">
          <Spinner
            animation="border"
            role="status"
            className="mr-2"
            size="sm"
          />
        </div>
      )}

      {showSearchIcon ? (
        <Icon icon="magnify" size="24" fill={NAVY} style={{ zIndex: 1 }} />
      ) : (
        !suggestions.loading &&
        !!inputValue &&
        showClearOption && (
          <Button
            variant="none"
            className={classNames(
              styles.close_button,
              'align-self-center ml-auto mr-1 flex-center p-0'
            )}
            onClick={() => setInputValue('')}
          >
            <Icon icon="close" className="fa-md" fill={SLATE_GRAY} />
          </Button>
        )
      )}

      <AutoCompleteSuggestions
        onSelect={handleSelect}
        suggestions={suggestions}
        suggestionItemClassName={suggestionItemClassName}
        suggestionContainerClassName={suggestionContainerClassName}
      />
    </div>
  )
}

export default LocationAutoComplete
